import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import Swal from 'sweetalert2';
import { apiHandler } from '../../api/apihandler';
import { STUDENT_GAME_SESSION_START, STUDENT_GAME_SESSION_SUBMIT } from '../../api/constants';

const ROUNDS_PER_SESSION = 10;
const MIN_DIFFICULTY = 1;
const MAX_DIFFICULTY = 5;
const CORRECT_STREAK_TO_ADVANCE = 3;
const WRONG_STREAK_TO_DROP = 2;

// The pre-fetched batch only spans starting_difficulty +/-1 (see
// StudentGamesController.sessionStart), so once the adaptive loop drifts
// difficulty outside that range there's nothing left at the exact tier -
// fall back to whatever unused item is closest to the target tier rather
// than dead-ending the session. Real per-session re-fetching is explicitly
// deferred (see the AI-Adaptive Practice Games plan's "explicitly deferred"
// section) in favor of a generous initial batch size.
function pickNextItem(items, usedIds, targetDifficulty) {
  const unused = items.filter((item) => !usedIds.has(item.id));
  if (unused.length === 0) return null;

  const exact = unused.filter((item) => item.difficulty_level === targetDifficulty);
  if (exact.length > 0) {
    return exact[Math.floor(Math.random() * exact.length)];
  }

  let closest = unused[0];
  let closestDiff = Math.abs(closest.difficulty_level - targetDifficulty);
  for (const item of unused) {
    const diff = Math.abs(item.difficulty_level - targetDifficulty);
    if (diff < closestDiff) {
      closest = item;
      closestDiff = diff;
    }
  }
  return closest;
}

function shuffle(array) {
  const copy = [...array];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const OPTION_COLORS = [
  'linear-gradient(135deg, #4CAF50, #2E7D32)',
  'linear-gradient(135deg, #2196F3, #1565C0)',
  'linear-gradient(135deg, #FF9800, #E65100)',
  'linear-gradient(135deg, #E91E63, #AD1457)',
];

export const AiAdaptiveGame = ({ gameData, categoryId, handleClose, onSessionEnd, actingStudentId = null }) => {
  const [phase, setPhase] = useState('loading'); // loading | playing | empty | error | finished
  const [sessionId, setSessionId] = useState(null);
  const [difficulty, setDifficulty] = useState(1);
  const [batch, setBatch] = useState([]);
  const [usedIds, setUsedIds] = useState(new Set());
  const [currentItem, setCurrentItem] = useState(null);
  const [shuffledOptions, setShuffledOptions] = useState([]);
  const [roundsPlayed, setRoundsPlayed] = useState([]);
  const [correctStreak, setCorrectStreak] = useState(0);
  const [wrongStreak, setWrongStreak] = useState(0);
  const [selectedKey, setSelectedKey] = useState(null);
  const [answerLocked, setAnswerLocked] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const questionStartedAt = useRef(null);
  const correctSound = useRef(new Audio('/sounds/correct.mp3'));
  const errorSound = useRef(new Audio('/sounds/error.mp3'));

  useEffect(() => {
    const savedVol = localStorage.getItem('tapGameVolume');
    if (savedVol !== null) setVolume(parseFloat(savedVol));
  }, []);

  useEffect(() => {
    startSession();
    // Stop any in-progress narration if the child closes the game mid-question.
    return () => window.speechSynthesis.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const speak = (text) => {
    if (isMuted || !text) return;
    try {
      // The browser queues utterances rather than replacing them - without
      // cancel() first, answering fast enough leaves earlier questions'
      // narration still playing (or queued) while a later question is
      // already on screen. Cancelling always wins the race, so the voice
      // the child hears always matches whatever question is currently shown.
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(text);
      utter.rate = 0.85;
      utter.volume = volume;
      window.speechSynthesis.speak(utter);
    } catch (e) {
      // speechSynthesis unavailable - silently skip narration.
    }
  };

  async function startSession() {
    setPhase('loading');
    try {
      const res = await apiHandler(STUDENT_GAME_SESSION_START, 'POST', {
        game_category_id: categoryId,
        ...(actingStudentId ? { student_id: actingStudentId } : {}),
      });

      if (res?.data?.response?.status !== 200) {
        setPhase('error');
        return;
      }

      const data = res.data.data;
      const items = data.items || [];

      if (items.length === 0) {
        setSessionId(data.session_id);
        setPhase('empty');
        return;
      }

      setSessionId(data.session_id);
      setDifficulty(data.starting_difficulty || 1);
      setBatch(items);
      loadNextQuestion(items, new Set(), data.starting_difficulty || 1);
    } catch (error) {
      console.error('Error starting adaptive session:', error);
      setPhase('error');
    }
  }

  function loadNextQuestion(items, used, targetDifficulty) {
    const next = pickNextItem(items, used, targetDifficulty);
    if (!next) {
      finishSession(used, roundsPlayed);
      return;
    }
    setCurrentItem(next);
    setShuffledOptions(shuffle(next.content?.options || []));
    setSelectedKey(null);
    setAnswerLocked(false);
    questionStartedAt.current = Date.now();
    setPhase('playing');
    setTimeout(() => speak(next.content?.promptText), 400);
  }

  function handleAnswer(option) {
    if (answerLocked || !currentItem) return;
    setAnswerLocked(true);
    setSelectedKey(option.key);

    const isCorrect = !!option.isCorrect;
    const responseTimeMs = Date.now() - (questionStartedAt.current || Date.now());

    if (!isMuted) {
      const sound = isCorrect ? correctSound.current : errorSound.current;
      sound.volume = volume;
      sound.currentTime = 0;
      sound.play().catch(() => {});
    }

    const round = {
      game_question_item_id: currentItem.id,
      difficulty_at_time: difficulty,
      is_correct: isCorrect,
      response_time_ms: responseTimeMs,
    };

    const updatedRounds = [...roundsPlayed, round];
    const updatedUsed = new Set(usedIds);
    updatedUsed.add(currentItem.id);

    const newCorrectStreak = isCorrect ? correctStreak + 1 : 0;
    const newWrongStreak = isCorrect ? 0 : wrongStreak + 1;

    let nextDifficulty = difficulty;
    if (newCorrectStreak >= CORRECT_STREAK_TO_ADVANCE) {
      nextDifficulty = Math.min(MAX_DIFFICULTY, difficulty + 1);
    } else if (newWrongStreak >= WRONG_STREAK_TO_DROP) {
      nextDifficulty = Math.max(MIN_DIFFICULTY, difficulty - 1);
    }
    const streaksResetByShift = nextDifficulty !== difficulty;

    setRoundsPlayed(updatedRounds);
    setUsedIds(updatedUsed);
    setCorrectStreak(streaksResetByShift ? 0 : newCorrectStreak);
    setWrongStreak(streaksResetByShift ? 0 : newWrongStreak);
    setDifficulty(nextDifficulty);

    setTimeout(() => {
      if (updatedRounds.length >= ROUNDS_PER_SESSION) {
        finishSession(updatedUsed, updatedRounds);
      } else {
        loadNextQuestion(batch, updatedUsed, nextDifficulty);
      }
    }, 900);
  }

  async function finishSession(used, finalRounds) {
    if (!finalRounds || finalRounds.length === 0) {
      onSessionEnd?.();
      return;
    }

    setPhase('submitting');
    try {
      const res = await apiHandler(STUDENT_GAME_SESSION_SUBMIT, 'POST', {
        session_id: sessionId,
        rounds: finalRounds,
        ...(actingStudentId ? { student_id: actingStudentId } : {}),
      });

      const totalCorrect = finalRounds.filter((r) => r.is_correct).length;
      const accuracy = Math.round((totalCorrect / finalRounds.length) * 100);
      const stars = accuracy >= 85 ? 3 : accuracy >= 60 ? 2 : 1;
      const endingDifficulty = res?.data?.data?.ending_difficulty || difficulty;

      confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });

      await Swal.fire({
        title: '🎉 Great Practice!',
        html: `
          <div style="text-align:center;">
            <div style="font-size:2rem;margin:0.5rem 0;">${'⭐'.repeat(stars)}</div>
            <p style="font-size:1.1rem;color:#4CAF50;font-weight:600;">${totalCorrect} of ${finalRounds.length} correct</p>
            <p style="color:#666;">Reached level ${endingDifficulty} of ${MAX_DIFFICULTY}</p>
          </div>
        `,
        icon: 'success',
        confirmButtonText: 'Done',
        confirmButtonColor: '#4CAF50',
        background: '#f0f8ff',
      });

      setPhase('finished');
      onSessionEnd?.();
    } catch (error) {
      console.error('Error submitting adaptive session:', error);
      setPhase('error');
    }
  }

  if (phase === 'loading' || phase === 'submitting') {
    return (
      <div className="ai-adaptive-board" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧠</div>
        <h5 style={{ color: '#3e2723', fontWeight: 800 }}>
          {phase === 'submitting' ? 'Saving your progress...' : 'Getting your practice ready...'}
        </h5>
      </div>
    );
  }

  if (phase === 'empty') {
    return (
      <div className="ai-adaptive-board" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🧩</div>
        <h5 style={{ color: '#3e2723', fontWeight: 800 }}>This practice set isn't ready yet</h5>
        <p style={{ color: '#6b4f3f' }}>Please check back soon - we're still preparing activities for this one!</p>
      </div>
    );
  }

  if (phase === 'error') {
    return (
      <div className="ai-adaptive-board" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
        <h5 style={{ color: '#3e2723', fontWeight: 800 }}>Something went wrong</h5>
        <p style={{ color: '#6b4f3f' }}>Please close this and try again.</p>
      </div>
    );
  }

  if (phase === 'finished') {
    return null;
  }

  return (
    <div className="ai-adaptive-board">
      <div className="ai-adaptive-header">
        <div className="ai-adaptive-badge">
          <span role="img" aria-label="level" style={{ fontSize: 20, marginRight: 6 }}>🧠</span>
          Level {difficulty}
        </div>
        <div className="ai-adaptive-progress">
          Question {Math.min(roundsPlayed.length + 1, ROUNDS_PER_SESSION)} of {ROUNDS_PER_SESSION}
        </div>
        <div className="ai-adaptive-volume">
          <span role="img" aria-label="volume" style={{ fontSize: 18, marginRight: 4 }}>🔊</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={volume}
            onChange={(e) => {
              const newVol = parseFloat(e.target.value);
              setVolume(newVol);
              localStorage.setItem('tapGameVolume', newVol);
            }}
            style={{ width: '80px', verticalAlign: 'middle' }}
          />
        </div>
      </div>

      <div className="ai-adaptive-prompt">
        <span>{currentItem?.content?.promptText}</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary ms-2"
          style={{ borderRadius: 12, padding: '2px 10px' }}
          title="Replay"
          onClick={() => speak(currentItem?.content?.promptText)}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18, verticalAlign: 'middle' }}>replay</span>
        </button>
      </div>

      {currentItem?.content?.stimulus && (
        <div className="ai-adaptive-stimulus">
          {Array.from({ length: currentItem.content.stimulus.count }).map((_, i) => {
            const stimulusUrl = currentItem?.resolvedImages?.[currentItem.content.stimulus.imageId];
            return stimulusUrl ? (
              <img key={i} src={stimulusUrl} alt="" style={{ width: 56, height: 56, margin: 4 }} />
            ) : (
              <span key={i} style={{ fontSize: '2rem', margin: 4 }}>🔵</span>
            );
          })}
        </div>
      )}

      <div className="ai-adaptive-options">
        {shuffledOptions.map((option, index) => {
          const imageUrl = option.imageId ? currentItem?.resolvedImages?.[option.imageId] : null;
          const isSelected = selectedKey === option.key;
          const showCorrect = answerLocked && option.isCorrect;
          const showWrong = answerLocked && isSelected && !option.isCorrect;

          return (
            <button
              key={option.key || index}
              type="button"
              disabled={answerLocked}
              onClick={() => handleAnswer(option)}
              className={`ai-adaptive-option-btn ${showCorrect ? 'is-correct' : ''} ${showWrong ? 'is-wrong' : ''}`}
              style={{ background: OPTION_COLORS[index % OPTION_COLORS.length] }}
            >
              {imageUrl && <img src={imageUrl} alt={option.label} style={{ width: 60, height: 60, marginBottom: 8, borderRadius: 12 }} />}
              <span>{option.label}</span>
              {showCorrect && <span style={{ marginLeft: 6 }}>✅</span>}
              {showWrong && <span style={{ marginLeft: 6 }}>❌</span>}
            </button>
          );
        })}
      </div>

      <style>{`
        .ai-adaptive-board {
          background: url('/image/board.png'), linear-gradient(135deg, rgba(62,62,62,0.3) 0%, rgba(15,15,14,0.41) 100%);
          background-size: cover, cover;
          background-position: center, center;
          border-radius: 32px;
          box-shadow: 0 6px 32px #e0c68a33;
          padding: 32px 20px 28px 20px;
          margin: 0 auto;
          max-width: 900px;
          width: 98%;
          min-height: 400px;
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          animation: board-pop 0.5s cubic-bezier(.68,-0.55,.27,1.55);
        }
        @keyframes board-pop {
          0% { transform: scale(0.9); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
        .ai-adaptive-header {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 20px;
        }
        .ai-adaptive-badge, .ai-adaptive-progress, .ai-adaptive-volume {
          background: rgba(255,255,255,0.88);
          border-radius: 14px;
          padding: 8px 16px;
          font-weight: 700;
          color: #b8860b;
          display: flex;
          align-items: center;
          box-shadow: 0 1px 4px rgba(224,198,138,0.10);
        }
        .ai-adaptive-prompt {
          background: rgba(255,255,255,0.92);
          border-radius: 20px;
          padding: 18px 28px;
          font-size: 1.4rem;
          font-weight: 700;
          color: #3e2723;
          text-align: center;
          margin-bottom: 28px;
          box-shadow: 0 2px 10px rgba(224,198,138,0.15);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .ai-adaptive-stimulus {
          background: rgba(255,255,255,0.85);
          border-radius: 20px;
          padding: 14px;
          margin-bottom: 24px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
        }
        .ai-adaptive-options {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
          gap: 20px;
          width: 100%;
        }
        .ai-adaptive-option-btn {
          border: none;
          border-radius: 24px;
          color: #fff;
          font-weight: 900;
          font-size: 1.4rem;
          padding: 28px 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 18px rgba(0,0,0,0.18);
          transition: transform 0.15s ease;
          cursor: pointer;
          font-family: 'Baloo 2', 'Fredoka One', 'Comic Sans MS', cursive;
        }
        .ai-adaptive-option-btn:not(:disabled):hover {
          transform: translateY(-6px) scale(1.04);
        }
        .ai-adaptive-option-btn:disabled {
          cursor: default;
          opacity: 0.75;
        }
        .ai-adaptive-option-btn.is-correct {
          outline: 4px solid #2E7D32;
          opacity: 1;
        }
        .ai-adaptive-option-btn.is-wrong {
          outline: 4px solid #C62828;
        }
        @media (max-width: 600px) {
          .ai-adaptive-board { padding: 18px 10px 18px 10px; }
          .ai-adaptive-prompt { font-size: 1.15rem; padding: 14px 16px; }
          .ai-adaptive-option-btn { font-size: 1.15rem; padding: 20px 8px; }
        }
      `}</style>
    </div>
  );
};

export default AiAdaptiveGame;
